
/** Represents one e-mail address (JSON equivalent of jakarta mail InternetAddress) */
export interface InternetAddress {
	personal?: string;
	address: string;
}

/**
 * RFC 822 "specials" that force a personal name to be quoted when serialized.
 * Matches jakarta mail's specialsNoDot set (dot is intentionally excluded so
 * names like "John Q. Public" stay unquoted).
 */
const SPECIALS = "()<>@,;:\\\"[]";

/** Strip surrounding double quotes and resolve backslash escapes inside them. */
function unquote(s: string): string {
	if (s.length < 2 || s[0] !== '"' || s[s.length - 1] !== '"') return s;
	const inner = s.substring(1, s.length - 1);
	let out = "";
	for (let i = 0; i < inner.length; i++) {
		const c = inner[i];
		if (c === "\\" && i + 1 < inner.length) out += inner[++i];
		else out += c;
	}
	return out;
}

/** True when the personal name contains a character that requires quoting. */
function needsQuoting(personal: string): boolean {
	for (const c of personal) {
		if (c < " " || SPECIALS.indexOf(c) >= 0) return true;
	}
	return false;
}

/** Wrap a personal name in double quotes, escaping backslash and quote chars. */
function quotePhrase(personal: string): string {
	let out = '"';
	for (const c of personal) {
		if (c === "\\" || c === '"') out += "\\";
		out += c;
	}
	return out + '"';
}

/**
 * Parse a single, non-encoded e-mail address.
 *
 *   "blesa@anneca.cz"                   -> { address: "blesa@anneca.cz" }
 *   "John Brown <brown@gmail.com>"      -> { personal: "John Brown", address: "brown@gmail.com" }
 *   "\"Master, Commander\" <mc@x.com>"  -> { personal: "Master, Commander", address: "mc@x.com" }
 *
 * Returns null for blank input.
 */
export function parseAddress(adr: string): InternetAddress | null {
	const s = adr.trim();
	if (s.length === 0) return null;

	// Locate the '<' that opens the address, ignoring any inside a quoted name.
	let ltIndex = -1;
	let inQuote = false;
	for (let i = 0; i < s.length; i++) {
		const c = s[i];
		if (c === "\\" && inQuote) { i++; continue; }
		if (c === '"') { inQuote = !inQuote; continue; }
		if (c === "<" && !inQuote) { ltIndex = i; break; }
	}

	if (ltIndex < 0) return { address: s };

	const gtIndex = s.indexOf(">", ltIndex + 1);
	const address = (gtIndex < 0 ? s.substring(ltIndex + 1) : s.substring(ltIndex + 1, gtIndex)).trim();
	const personal = unquote(s.substring(0, ltIndex).trim());

	return personal.length > 0 ? { personal, address } : { address };
}

/** Serialize an address to a string, quoting the personal name when required. */
export function addressToString(adr: InternetAddress): string {
	const personal = adr.personal?.trim();
	if (!personal) return adr.address;
	const name = needsQuoting(personal) ? quotePhrase(personal) : personal;
	return name + " <" + adr.address + ">";
}

/** Parse multiple comma-separated addresses (commas inside quotes are kept). */
export function parseAddresses(adrs: string): InternetAddress[] {
	const result: InternetAddress[] = [];
	let start = 0;
	let inQuote = false;
	for (let i = 0; i < adrs.length; i++) {
		const c = adrs[i];
		if (c === "\\" && inQuote) { i++; continue; }
		if (c === '"') { inQuote = !inQuote; continue; }
		if (c === "," && !inQuote) {
			const part = parseAddress(adrs.substring(start, i));
			if (part) result.push(part);
			start = i + 1;
		}
	}
	const last = parseAddress(adrs.substring(start));
	if (last) result.push(last);
	return result;
}

/** Syntactic check of the bare e-mail part: single '@', non-empty local/domain, dotted domain, no whitespace. */
function isValidEmail(addr: string): boolean {
	const at = addr.indexOf("@");
	if (at <= 0) return false;                            // need a non-empty local part before '@'
	if (addr.indexOf("@", at + 1) >= 0) return false;     // exactly one '@'
	for (const c of addr) {
		if (c === " " || c === "\t" || c === "\n" || c === "\r") return false;
	}
	const domain = addr.substring(at + 1);
	const dot = domain.indexOf(".");
	return dot > 0 && dot < domain.length - 1;            // dot present, not at either edge
}

/**
 * Test whether a string looks like a comma-separated list of e-mail addresses
 * (as opposed to arbitrary text). Useful to decide how to handle a clipboard
 * paste: if true, the same string can be fed to parseAddresses().
 */
export function validAddressList(s: string): boolean {
	const list = parseAddresses(s);
	if (list.length === 0) return false;
	for (const a of list) {
		if (!isValidEmail(a.address)) return false;
	}
	return true;
}
